<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_excursion_report')"
                            :default-settings="defaultSettings"
                            :prepare-settings-function="prepareSettingsFunction"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :schema="settingsSchema"
                            :use-group-reporting-option="groupReportingEnabled"
                            :report-icon="settingsSchema.icon"
                            report-name="excursion-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
                            @reset-settings="resetSelections"
    >
        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <!-- Savable-only settings (the recurring send schedule) belong to a
                 saved template, not to a one-off run, so they are shown only
                 when editing a template. The send cadence is a property of the
                 report's scheduled delivery rather than of the report's content,
                 and surfacing it on the On-Demand form read as a setting that
                 would change the report you were about to generate. -->
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <interval-selection   v-model:end-of-interval="endOfInterval"
                                        v-model:interval="interval"
                                        v-model:selected-meters="selectedMeters"
                                        v-model:semi-month-half="semiMonthHalf"
                                        :meters="metersByGroup[props.groupId]"
                                        :meter-tree="meterTree"
                                        :footer-mode="props.footerMode ?? 'none'"
                                        :wide="true"
                                        :use-range="! props.reportTemplateId"
                                        v-model:range-start="rangeStart"
                                        v-model:range-end="rangeEnd"
                                        :is-running="runningReport"
                                        :ran-report="reportSuccessfullySent"
                                        :running-errors="runReportErrors"
                                        :max-date="maxDate"
                                        :min-date="minDate"
                                        :timezone="timezone"
                                        @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'

import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import {Intervals} from "./Intervals";
import {useGroupReport, GENERIC_RUN_ERROR} from "@/composables/useGroupReport";

import IntervalSelection from "./IntervalSelection";
import GroupReportTemplate from "./GroupReportTemplate";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import excursionSettingsSchema from "./settings/schemas/excursion";
import {toReportDate} from "./settings/ReportDateRange.vue";

export default {
    name: "GroupExcursionReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        receiveWeekday: {
            type: Number,
            default: 1,
        },
        interval: {
            type: String,
            default: 'monthly',
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        metersByGroup: {
            type: Object,
            default() {
                return {}
            }
        },
        meterTree: {
            type: Array,
            default() {
                return []
            }
        },
        timezone: {
            type: String,
        },
    },
    setup(props) {
        // Excursion-only run state the shared composable does not own: an
        // explicit date range from the run form and the semi-month half.
        const rangeStart = ref(null)
        const rangeEnd = ref(null)
        const semiMonthHalf = ref('first')

        const report = useGroupReport(excursionSettingsSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            // Excursion's extra POST fields, layered over the composable's
            // common base (reportTemplateId/forGroup or interval; end; meters):
            //  - start/end: on an On-Demand run, the calendar range from the run
            //    form. toReportDate() posts a plain date, read in the company
            //    timezone, so the day the user picked cannot be shifted by a UTC
            //    conversion in transit or by the user's own browser zone.
            //    A saved template's manual run posts no start, so the controller
            //    derives the window from the template's interval -- the same
            //    window its scheduled sends produce. Otherwise running a monthly
            //    template mid-month would report month-to-date instead of the
            //    full month it is defined to cover.
            //  - settings: the settings blob on a manual (non-template) run.
            //  - semiMonthHalf: only when the interval is semi-monthly.
            extraParams: (state, reportTemplateId) => {
                const params = {
                    meters: state.selectedMeters.value,
                }

                if (reportTemplateId === null) {
                    params.start = toReportDate(rangeStart.value, props.timezone)
                    params.end = toReportDate(rangeEnd.value, props.timezone) ?? state.endOfInterval.value
                }

                if (state.currentReportTemplateId.value === null) {
                    params.settings = state.settings.value
                }

                // NOTE: intentionally inert, preserved from the pre-composable
                // code. endOfInterval is a Date object and SEMI_MONTHLY is the
                // string 'semi-monthly', so this comparison is always false and
                // semiMonthHalf is never posted. Do NOT "fix" this to compare
                // against `interval` without running the before/after PDF gate:
                // it would change what the controller receives.
                if (state.endOfInterval.value === Intervals.SEMI_MONTHLY) {
                    params.semiMonthHalf = semiMonthHalf.value
                }

                return params
            },
        })

        return {
            ...report,
            rangeStart,
            rangeEnd,
            semiMonthHalf,
        }
    },
    data() {
        return {
            prepareSettingsFunction: null,
        }
    },
    created() {
        this.prepareSettingsFunction = (settings) => {
            // If the date is null, set it to 07:00 and 19:00 respectively.
            if (settings['logTime1'] === null) {
                settings['logTime1'] = '07:00'
            }
            if (settings['logTime2'] === null) {
                settings['logTime2'] = '19:00'
            }

            return settings
        }
    },
    methods: {
        /**
         * Server messages are sometimes raw internals (an HTML error page, or a
         * stack/stderr dump from PDF rendering). Those are meaningless to a user
         * and leak implementation detail, so anything long or non-prose falls
         * back to a generic message.
         */
        presentableMessage(message) {
            if (typeof message !== 'string' || message.trim() === '') {
                return GENERIC_RUN_ERROR
            }

            const trimmed = message.trim()

            // Anything that looks like machine output rather than a sentence
            // written for a user: HTML pages, shell/stderr dumps, stack traces,
            // progress bars, file paths, or just implausibly long text.
            const looksLikeInternals = trimmed.startsWith('<')
                || trimmed.startsWith('{')
                || trimmed.length > 160
                || /stderr|stdout|stack trace|exit status|Warning:|Error:|Exception|\[=+|\/var\/www|node_modules|\bat [A-Z]\w+\./i.test(trimmed)

            return looksLikeInternals ? GENERIC_RUN_ERROR : trimmed
        },
        resetSelections() {
            this.selectedMeters = []
        },
    },
    computed: {
        settingsSchema() {
            return excursionSettingsSchema
        },
        // Normalise whatever the failed response body was into displayable
        // messages: a 500 returns an HTML page, validation returns an object.
        runReportErrors() {
            const value = this.errorSending

            if (! value || (Array.isArray(value) && value.length === 0)) {
                return []
            }

            // Validation failures are the one case with messages worth showing
            // verbatim: they are written for the user and name a real field.
            if (! Array.isArray(value) && typeof value === 'object' && value.errors) {
                return Object.values(value.errors).flat().map((item) => this.presentableMessage(item))
            }

            const candidates = Array.isArray(value)
                ? value
                : [typeof value === 'object' ? value.message : value]

            const messages = candidates
                .map((item) => this.presentableMessage(item))
                // Collapse the common case where every line reduces to the same
                // generic text, so the user sees it once rather than N times.
                .filter((item, index, all) => all.indexOf(item) === index)

            return messages.length > 0 ? messages : [GENERIC_RUN_ERROR]
        },
    },
}
</script>
